import React from 'react';
import { OverlayTrigger, Tooltip, Button } from 'react-bootstrap';
import { usePermissions } from '../context/PermissionsContext';
import LoadingSpinner from './LoadingSpinner';
import AccessDenied from './AccessDenied';
import './PermissionGuard.css';

export const PermissionGuard = ({ 
    permission, 
    children, 
    fallback = null,
    showDisabled = false,
    tooltip = "You don't have permission to perform this action"
}) => {
    const { hasPermission, isPermissionsLoading } = usePermissions();

    if (isPermissionsLoading) {
        return showDisabled ? (
            <Button disabled>Loading...</Button>
        ) : fallback;
    }

    const hasAccess = hasPermission(permission);

    if (hasAccess) {
        return children;
    }

    if (!showDisabled) {
        return fallback;
    }

    // showDisabled expects exactly one element child to clone as disabled
    if (!React.isValidElement(children) || React.Children.count(children) !== 1) {
        return fallback;
    }

    // Wrap disabled element with tooltip
    return (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`permission-tooltip-${permission}`}>{tooltip}</Tooltip>}
        >
            <span className="d-inline-block">
                {React.cloneElement(children, {
                    disabled: true,
                    className: `${children.props.className || ''} permission-disabled`,
                    style: { ...children.props.style, pointerEvents: 'none', opacity: 0.6 }
                })}
            </span>
        </OverlayTrigger>
    );
};

export const RoutePermissionGuard = ({ permission, role, children, fallback = null, showPermission = false, message, section }) => {
  const { hasPermission, hasRole, isPermissionsLoading } = usePermissions();

  if (isPermissionsLoading) {
    return (
      <div className="permissions-loading-banner">
        <LoadingSpinner size="md" variant="primary" />
      </div>
    );
  }

  if (permission && !hasPermission(permission)) {
    if (fallback) return fallback;
    return (
      <AccessDenied
        permission={permission}
        section={section}
        showPermission={showPermission}
        message={message}
      />
    );
  }

  if (role && !hasRole(role)) {
    if (fallback) return fallback;
    return (
      <AccessDenied
        role={role}
        section={section}
        message={message}
      />
    );
  }

  return children;
};

export const withPermission = (permission) => (Component) => (props) => (
    <PermissionGuard permission={permission}>
        <Component {...props} />
    </PermissionGuard>
);

export default PermissionGuard;